import { useAuth } from '../hooks/useAuth'

interface Section {
  title: string
  body: string[]
}

const SELLER_SECTIONS: Section[] = [
  {
    title: 'Panel Principal',
    body: [
      'Muestra un resumen de tu actividad: tendencia de ventas, ventas por producto, tus top clientes, ventas por distrito, cartera de cobranza y clientes inactivos.',
      'Puedes elegir qué gráficos ver desde "Configuración" — desmarcar un panel solo lo oculta para ti, no afecta a nadie más.',
    ],
  },
  {
    title: 'Clientes',
    body: [
      'Registra nuevos clientes con nombre, apodo, dirección, distrito, teléfono, DNI/RUC y modo de pago (efectivo o crédito).',
      'El tipo de negocio se puede marcar como "Otro" cuando ninguna categoría existente aplica, describiéndolo libremente.',
      'La sección "Clientes Inactivos" te avisa quiénes no te compran desde hace tiempo, para que los priorices en tu próxima visita.',
    ],
  },
  {
    title: 'Seguimiento',
    body: [
      'Muestra la ubicación de tus clientes en el mapa, útil para planear rutas de visita.',
    ],
  },
  {
    title: 'Productos',
    body: [
      'Aquí armas tu propio catálogo: eliges qué productos del catálogo maestro vendes, en qué presentación, y defines tu "precio real" de venta (puede diferir del precio recomendado por administración).',
    ],
  },
  {
    title: 'Ventas',
    body: [
      'Para registrar una venta: selecciona o crea un cliente, agrega uno o más productos al carrito (cada uno con su presentación, cantidad y datos de pago), y confirma — se registran todas las líneas juntas.',
      'Si una venta queda con saldo pendiente, puedes registrar pagos parciales después desde el botón "Pagar".',
      'Si el administrador te quitó el permiso de editar ventas, los botones de edición no aparecerán — solo podrás ver el historial.',
    ],
  },
  {
    title: 'Pedidos',
    body: [
      'Un pedido es un acuerdo verbal con el cliente para entregar productos más adelante — todavía no es una venta.',
      'Al crear el pedido fijas el precio de cada producto en ese momento ("precio pactado"), la fecha del acuerdo y, si aplica, la fecha prometida de entrega.',
      'Cada producto del pedido tiene su propio estado: "Pactado" (esperando), "Entregado" o "Cancelado". Puedes entregar y cobrar unos productos del pedido mientras otros siguen pendientes o se cancelan, sin afectar al resto.',
      'Al marcar un producto como "Entregado" se abre automáticamente el formulario para registrarlo como venta, usando el precio que ya habías pactado.',
      'Al cancelar un producto del pedido, debes escribir el motivo — queda guardado como referencia.',
    ],
  },
  {
    title: 'Mensajes',
    body: [
      'Chat directo con administración. El número junto al ícono indica mensajes sin leer.',
    ],
  },
  {
    title: 'Configuración',
    body: [
      'Elige qué paneles del dashboard quieres ver. Es solo para tu vista, no cambia nada para otros vendedores ni para administración.',
    ],
  },
  {
    title: 'Perfil',
    body: [
      'Cambia tu contraseña o tu correo de acceso desde aquí.',
    ],
  },
]

const ADMIN_SECTIONS: Section[] = [
  {
    title: 'Panel Principal',
    body: [
      'Resumen general del negocio: tendencia de ventas, ingresos por producto, top ventas, zonas de venta y brecha entre precio recomendado y precio real (por vendedor o por producto).',
      'Puedes filtrar todo el panel por un vendedor específico para un análisis individual.',
    ],
  },
  {
    title: 'Productos',
    body: [
      'Catálogo maestro de productos que los vendedores pueden agregar a su propio catálogo.',
    ],
  },
  {
    title: 'Ventas',
    body: [
      'Vista de solo lectura de todas las ventas registradas por todos los vendedores, con filtros por vendedor, fecha, cliente y distrito.',
      'La edición de una venta la hace el propio vendedor que la registró, no administración.',
    ],
  },
  {
    title: 'Pedidos',
    body: [
      'Vista de solo lectura de los pedidos (acuerdos verbales) de todos los vendedores, con filtros por vendedor, estado de cada producto (pactado/entregado/cancelado), fecha, cliente y distrito.',
      'Marcar un producto como entregado o cancelarlo lo hace el vendedor desde su propia vista de Pedidos.',
    ],
  },
  {
    title: 'Rutas',
    body: [
      'Creación y asignación de rutas de reparto a vendedores, con confirmación de check-in.',
    ],
  },
  {
    title: 'Quejas',
    body: [
      'Registro y resolución de quejas de clientes.',
    ],
  },
  {
    title: 'Colaboradores',
    body: [
      'Alta de nuevos colaboradores, activar/desactivar cuentas, y control de permisos: puedes revocarle a un vendedor la capacidad de editar clientes o de editar ventas ya registradas — el cambio aplica de inmediato, sin que necesite volver a iniciar sesión.',
      'Cada colaborador puede tener uno o más paneles asignados (Vendedor, Logística, RRHH, Finanzas), marcados con checkboxes. Un colaborador puede tener varios paneles a la vez — por ejemplo, Vendedor y Logística simultáneamente.',
    ],
  },
  {
    title: 'Mensajes',
    body: [
      'Chat directo con cada vendedor por separado.',
    ],
  },
  {
    title: 'Configuración',
    body: [
      'Elige qué paneles del dashboard de administración quieres ver. Es solo para tu vista.',
    ],
  },
  {
    title: 'Perfil',
    body: [
      'Cambia tu contraseña o tu correo de acceso desde aquí.',
    ],
  },
]

export default function HelpPage() {
  const { user } = useAuth()
  const sections = user?.role === 'admin' ? ADMIN_SECTIONS : SELLER_SECTIONS

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-2">Ayuda / Tutorial</h1>
      <p className="text-sm text-espresso-500 mb-6">
        Guía rápida de cada sección del portal para tu rol ({user?.role === 'admin' ? 'administrador' : 'vendedor'}).
      </p>

      <div className="space-y-3">
        {sections.map((section, i) => (
          <details
            key={section.title}
            open={i === 0}
            className="bg-cream-50 shadow rounded-lg overflow-hidden group"
          >
            <summary className="cursor-pointer px-4 py-3 font-medium text-espresso-800 hover:bg-cream-50 list-none flex items-center justify-between">
              {section.title}
              <span className="text-espresso-400 text-sm group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="px-4 pb-4 space-y-2 text-sm text-espresso-600 border-t border-espresso-100 pt-3">
              {section.body.map((paragraph, j) => (
                <p key={j}>{paragraph}</p>
              ))}
            </div>
          </details>
        ))}
      </div>
    </div>
  )
}
